// frontend/src/components/UserList.js
// Componente para mostrar una tabla de usuarios con funcionalidades de añadir, editar y eliminar.

import React, { useEffect, useState } from 'react';
import { useUserStore } from '../store/userStore';
import { useAuthStore } from '../store/authStore';
import LoadingSpinner from './LoadingSpinner';
import CustomConfirmDialog from './CustomConfirmDialog';
import { PencilIcon, TrashIcon, PlusCircleIcon, ShieldCheckIcon, UserIcon } from '@heroicons/react/24/solid';

/**
 * @component UserList
 * @description Muestra una tabla de usuarios con opciones para gestionarlos.
 * @param {object} props - Propiedades del componente.
 * @param {function} props.onAddUser - Callback para iniciar el modo de añadir usuario.
 * @param {function(object)} props.onEditUser - Callback para iniciar el modo de edición de un usuario.
 */
function UserList({ onAddUser, onEditUser }) {
  const { users, fetchUsers, deleteUser, isLoadingUsers } = useUserStore();
  const { user: currentUser } = useAuthStore((state) => state);

  // Estado para la confirmación de eliminación
  const [showConfirm, setShowConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Cargar usuarios al montar el componente
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Función para abrir el diálogo de confirmación de eliminación
  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowConfirm(true);
  };

  // Función para ejecutar la eliminación después de la confirmación
  const handleDeleteConfirmed = async () => {
    if (userToDelete) {
      await deleteUser(userToDelete.id);
      setUserToDelete(null);
      setSelectedUserId(null);
    }
    setShowConfirm(false);
  };

  const toggleSelectedUser = (userId) => {
    setSelectedUserId((currentUserId) => currentUserId === userId ? null : userId);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="w-full">
      <div className="flex justify-end mb-4">
        <button
          onClick={onAddUser}
          className="px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center space-x-2 transition-colors"
        >
          <PlusCircleIcon className="h-5 w-5" />
          <span>Añadir Usuario</span>
        </button>
      </div>

      {isLoadingUsers ? (
        <div className="flex justify-center items-center h-48">
          <LoadingSpinner size="lg" />
        </div>
      ) : users.length === 0 ? (
        <p className="text-center text-gray-600 py-10">No hay usuarios para mostrar. ¡Añade uno!</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="hidden grid-cols-[1.6fr_1.8fr_0.8fr_1fr] gap-4 border-b border-gray-200 bg-gray-50 px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 lg:grid">
            <span>Nombre</span>
            <span>Correo</span>
            <span>Rol</span>
            <span>Creación</span>
          </div>
          <div className="divide-y divide-gray-200">
            {users.map((user) => {
              const isSelected = selectedUserId === user.id;
              const isCurrentUser = currentUser?.id === user.id;

              return (
                <div key={user.id} className="bg-white">
                  <button
                    type="button"
                    onClick={() => toggleSelectedUser(user.id)}
                    className={`block w-full px-4 py-4 text-left transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${isSelected ? 'bg-blue-50/70' : ''}`}
                  >
                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.6fr_1.8fr_0.8fr_1fr] lg:items-center lg:gap-4">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-900">{user.displayName || user.username}</p>
                        <p className="mt-1 text-xs text-gray-500">{isCurrentUser ? 'Sesión actual' : 'Usuario local'}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm text-gray-700">{user.upn || user.username}</p>
                      </div>
                      <div>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                          user.role === 'admin' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {user.role === 'admin' ? <ShieldCheckIcon className="mr-1 h-4 w-4"/> : <UserIcon className="mr-1 h-4 w-4"/>}
                          {user.role}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{formatDate(user.createdAt)}</p>
                      </div>
                    </div>
                  </button>
                  {isSelected && (
                    <div className="border-t border-blue-100 bg-blue-50/80 px-4 py-3">
                      <div className="flex flex-col gap-3 rounded-md border border-blue-100 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">Acciones para este usuario</p>
                          <p className="text-xs text-slate-500">{user.displayName || user.username} · {user.upn || user.username}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button type="button" onClick={() => onEditUser(user)} className="inline-flex items-center gap-2 rounded-md border border-indigo-200 bg-white px-3 py-2 text-sm font-medium text-indigo-700 shadow-sm transition-colors hover:bg-indigo-50">
                            <PencilIcon className="h-4 w-4" />
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(user)}
                            disabled={isCurrentUser}
                            className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700 shadow-sm transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400 disabled:hover:bg-white"
                            title={isCurrentUser ? 'No puedes eliminarte a ti mismo' : 'Eliminar usuario'}
                          >
                            <TrashIcon className="h-4 w-4" />
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <CustomConfirmDialog
        show={showConfirm}
        message={`¿Estás seguro de que quieres eliminar al usuario "${userToDelete?.displayName || userToDelete?.username}"? Esta acción no se puede deshacer.`}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setShowConfirm(false)}
      />
    </div>
  );
}

export default UserList;
